import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Add, DeleteOutlined, Remove } from '@mui/icons-material';
import { Product } from 'src/types/common';
import { useCartContext } from 'src/context/CartProvider/useCartContext';
import { useState } from 'react';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import { EventNamesMap, useAmplitude } from 'src/context/AmplitudeProvider';
import { parseToNumberOrNull } from 'src/utils/numbers/parteToNumberOrNull';

type AddToCartButtonProps = {
  product: Product;
  size?: 'small' | 'large' | 'medium';
  variant?: 'contained' | 'icon';
  max: number;
  min?: number;
  disabled?: boolean;
};

const AddToCartButton = ({
  product,
  max: availableQuantity,
  min = 0,
  size = 'large',
  variant = 'contained',
  disabled,
}: AddToCartButtonProps) => {
  const { isB2b, user } = useAuthContext();
  const isIcon = variant === 'icon';
  const { logEvent } = useAmplitude();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    addProductToCart,
    removeProductFromCart,
    cartproducts,
    total_with_coupon,
  } = useCartContext();

  const { product: productDetails, price, discount_price, b2b_price } = product;
  const productPrice = isB2b && b2b_price ? b2b_price : price;
  const discountPrice = isB2b ? null : discount_price;

  const cartProduct = cartproducts.find(
    (cartProduct) => cartProduct.product.pk === productDetails.pk
  );
  const currentQuantity = cartProduct?.quantity || 0;

  const handleQuantityInputChange = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>,
    change: 1 | -1
  ) => {
    event.stopPropagation();
    setIsUpdating(true);

    let newQuantity = currentQuantity + change;

    if (availableQuantity === 0 && change === -1) {
      newQuantity = 0;
    }

    const cartEventData = {
      email: user?.email as string,
      store_slug: user?.main_address?.store_data?.slug_name || 'revolucion',
      product_name: productDetails.name,
      product_pk: productDetails.pk,
      has_discount: !!discountPrice,
      price: Number(productPrice),
      discount_price: parseToNumberOrNull(discountPrice),
      is_b2b: !!isB2b,
    };

    const eventName =
      change === -1
        ? EventNamesMap.RemoveProductFromCart
        : EventNamesMap.AddProductToCart;

    user &&
      logEvent(eventName, {
        ...cartEventData,
        cart_id: user.active_cart!,
        is_coupon_applied: !!total_with_coupon,
        quantity: newQuantity,
      });

    if (newQuantity <= 0) {
      handleRemoveFromCart().then(() => {
        user &&
          user.active_cart &&
          logEvent(EventNamesMap.RemoveProductFromCart, {
            ...cartEventData,
            cart_id: user.active_cart,
            is_coupon_applied: null,
            quantity: newQuantity,
          });
        setIsUpdating(false);
      });
      return;
    }

    if (newQuantity > availableQuantity) {
      if (change === 1) return;

      newQuantity = availableQuantity;
    }

    handleCartQuantityChange(newQuantity)
      .then((cart) => {
        if (cart) {
          logEvent(EventNamesMap.AddToCartResult, {
            status: 'success',
            product_id: productDetails.pk,
          } as never);
        }

        setIsUpdating(false);
      })
      .catch((e) => {
        if (change === 1) {
          const message = (e && (e.message || String(e))) as string;
          let error_reason: string | undefined = undefined;
          if (message?.toLowerCase().includes('stock'))
            error_reason = 'out_of_stock';
          else if (message?.toLowerCase().includes('quantity'))
            error_reason = 'invalid_quantity';

          logEvent(EventNamesMap.AddToCartResult, {
            status: 'failure',
            product_id: productDetails.pk,
            ...(error_reason ? { error_reason } : { error_reason: e.message }),
          });
        }
        setIsUpdating(false);
      });
  };

  const handleCartQuantityChange = async (quantity: number) => {
    const finalPrice =
      !!discountPrice && Number(discountPrice) > 0
        ? discountPrice
        : productPrice;

    return await addProductToCart?.({
      product: productDetails,
      price: Number(finalPrice),
      quantity,
    });
  };

  const handleRemoveFromCart = async () => {
    if (!cartProduct) return;
    await removeProductFromCart?.(productDetails.pk);
  };

  const isInDryLaw = product.dry_law;

  if (isInDryLaw) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          opacity: 1,
          border: '2px solid red',
          borderRadius: 2,
          px: 1,
          py: 0.5,
        }}
      >
        <Typography
          color="error"
          fontWeight="fontWeightBold"
          width="100%"
          textAlign="center"
        >
          Ley seca
        </Typography>
      </Box>
    );
  }

  return !currentQuantity || currentQuantity <= 0 ? (
    <Button
      onClick={(e) => handleQuantityInputChange(e, 1)}
      color="primary"
      variant="contained"
      disabled={!availableQuantity || disabled}
      fullWidth={!isIcon}
      size={size}
      autoFocus={false}
      sx={{
        py: isIcon ? (availableQuantity ? 0.5 : 0) : 1,
        px: isIcon ? 0.5 : undefined,
        minWidth: isIcon ? 0 : undefined,
        height: 'fit-content',
        borderRadius: isIcon ? 1.5 : undefined,
        [`.MuiButtonBase-root-MuiButton-root.Mui-disabled`]: {
          backgroundColor: 'black',
        },
      }}
    >
      {availableQuantity ? (
        isIcon ? (
          isUpdating ? (
            <CircularProgress size={'25px'} />
          ) : (
            <Add fontSize={size} />
          )
        ) : (
          'Agregar al carrito'
        )
      ) : (
        'Sin stock'
      )}
    </Button>
  ) : (
    <Button
      autoFocus={false}
      size={size}
      variant="contained"
      fullWidth={!isIcon}
      sx={{
        pointerEvents: 'none',
        paddingY: size === 'small' ? 0 : undefined,
        height: 'fit-content',
        borderRadius: isIcon ? 1.5 : undefined,
      }}
      startIcon={
        currentQuantity >= min && (
          <Box
            onClick={(e) => handleQuantityInputChange(e, -1)}
            sx={{
              pointerEvents: 'all',
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}
            color="inherit"
          >
            {currentQuantity > 1 ? (
              <Remove fontSize={size} />
            ) : (
              <DeleteOutlined fontSize={size} />
            )}
          </Box>
        )
      }
      endIcon={
        currentQuantity < availableQuantity && (
          <Box
            onClick={(e) => handleQuantityInputChange(e, 1)}
            sx={{
              pointerEvents: 'all',
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}
            color="inherit"
          >
            <Add fontSize={size} />
          </Box>
        )
      }
    >
      <Typography color="inherit" fontWeight="fontWeightMedium" width="100%">
        {currentQuantity}
      </Typography>
    </Button>
  );
};

export default AddToCartButton;
